/**
 * Fuel sensor calibration utilities.
 *
 * Provides linear interpolation mapping from raw sensor values to real volume
 * or percentage outputs. Calibration tables are stored as JSON strings in
 * device.attributes.fuelCalibration and contain up to 20 calibration points.
 *
 * Schema: { unit: 'liters' | 'gallons' | 'percent', points: [{ raw, output }, ...] }
 */

import { downloadCSV } from './export.js';

// ---------------------------------------------------------------------------
// parseFuelCalibration
// ---------------------------------------------------------------------------

/**
 * Parse a raw fuelCalibration attribute value into a structured object.
 *
 * Accepts: null/undefined, JSON string, or an already-parsed object.
 * Returns null when the value is empty/invalid or points array is empty.
 *
 * @param {string|object|null|undefined} raw
 * @returns {{ unit: string, points: Array<{raw: number, output: number}> }|null}
 */
export function parseFuelCalibration(raw) {
  if (raw == null) return null;

  // Already an object — return as-is (idempotent)
  if (typeof raw === 'object') {
    if (!Array.isArray(raw.points) || raw.points.length === 0) return null;
    return raw;
  }

  // Parse JSON string
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.points) || parsed.points.length === 0) return null;
    return {
      unit: parsed.unit || 'liters',
      points: parsed.points,
    };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// applyFuelCalibration
// ---------------------------------------------------------------------------

/**
 * Apply a calibration table to a raw sensor value using linear interpolation.
 *
 * - If rawValue is null → returns null
 * - If calibration is null or has no points → returns rawValue (passthrough)
 * - Values below lowest raw point are clamped to lowest output
 * - Values above highest raw point are clamped to highest output
 *
 * @param {number|null} rawValue
 * @param {{ points: Array<{raw: number, output: number}> }|null} calibration
 * @returns {number|null}
 */
export function applyFuelCalibration(rawValue, calibration) {
  if (rawValue === null || rawValue === undefined) return null;
  if (!calibration || !Array.isArray(calibration.points) || calibration.points.length === 0) {
    return rawValue;
  }

  // Sort points by raw ascending
  const sorted = [...calibration.points].sort((a, b) => a.raw - b.raw);

  // Single-point: always return that output
  if (sorted.length === 1) return sorted[0].output;

  // Clamp below
  if (rawValue <= sorted[0].raw) return sorted[0].output;

  // Clamp above
  if (rawValue >= sorted[sorted.length - 1].raw) return sorted[sorted.length - 1].output;

  // Find bracket
  for (let i = 0; i < sorted.length - 1; i++) {
    const lo = sorted[i];
    const hi = sorted[i + 1];
    if (rawValue >= lo.raw && rawValue <= hi.raw) {
      const t = (rawValue - lo.raw) / (hi.raw - lo.raw);
      return lo.output + t * (hi.output - lo.output);
    }
  }

  // Fallback (should never reach here)
  return rawValue;
}

// ---------------------------------------------------------------------------
// validateCalibrationPoints
// ---------------------------------------------------------------------------

/**
 * Validate an array of calibration points.
 *
 * Rules:
 * - Maximum 20 points
 * - No duplicate raw values
 *
 * @param {Array<{raw: number, output: number}>} points
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateCalibrationPoints(points) {
  const errors = [];

  if (points.length > 20) {
    errors.push('Maximum 20 calibration points allowed');
  }

  const raws = points.map((p) => p.raw);
  const unique = new Set(raws);
  if (unique.size < raws.length) {
    errors.push('Duplicate raw values not allowed');
  }

  return { valid: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// isNonMonotonic
// ---------------------------------------------------------------------------

/**
 * Check whether a calibration point array has non-monotonically increasing output values.
 *
 * Points are sorted by raw value before the check. Returns true if any output
 * value is strictly less than the previous output (unusual tank shape warning).
 *
 * @param {Array<{raw: number, output: number}>} points
 * @returns {boolean}
 */
export function isNonMonotonic(points) {
  if (points.length < 2) return false;
  const sorted = [...points].sort((a, b) => a.raw - b.raw);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].output < sorted[i - 1].output) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// parseCalibrationCSV
// ---------------------------------------------------------------------------

/**
 * Parse a two-column CSV text (first row is header: raw,output) into an array
 * of calibration points.
 *
 * - Strips surrounding quotes from cell values
 * - Skips rows where either value parses to NaN
 * - Handles CRLF and LF line endings
 * - Skips the first (header) row
 *
 * @param {string} text - Raw CSV file content
 * @returns {Array<{raw: number, output: number}>}
 */
export function parseCalibrationCSV(text) {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  const result = [];

  // Skip header row (index 0)
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const cols = line.split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
    if (cols.length < 2) continue;

    const raw = parseFloat(cols[0]);
    const output = parseFloat(cols[1]);
    if (isNaN(raw) || isNaN(output)) continue;

    result.push({ raw, output });
  }

  return result;
}

// ---------------------------------------------------------------------------
// exportCalibrationCSV
// ---------------------------------------------------------------------------

/**
 * Export calibration points to a CSV file download.
 *
 * @param {string} deviceName - Used in the filename
 * @param {Array<{raw: number, output: number}>} points
 */
export function exportCalibrationCSV(deviceName, points) {
  const rows = points.map((p) => [p.raw, p.output]);
  const safeName = deviceName.replace(/[^a-zA-Z0-9_-]/g, '_');
  downloadCSV(['raw', 'output'], rows, `calibration_${safeName}.csv`);
}

// ---------------------------------------------------------------------------
// buildCurveData
// ---------------------------------------------------------------------------

/**
 * Generate 100-step interpolated curve data for Recharts preview.
 *
 * Given a set of calibration points, produces evenly spaced raw values from
 * min to max and computes the interpolated output for each step.
 *
 * @param {Array<{raw: number, output: number}>} points
 * @returns {Array<{raw: number, output: number}>}
 */
export function buildCurveData(points) {
  if (!points || points.length === 0) return [];

  const sorted = [...points].sort((a, b) => a.raw - b.raw);

  if (sorted.length === 1) {
    return [{ raw: sorted[0].raw, output: sorted[0].output }];
  }

  const minRaw = sorted[0].raw;
  const maxRaw = sorted[sorted.length - 1].raw;
  const steps = 100;
  const result = [];

  for (let i = 0; i < steps; i++) {
    const raw = minRaw + (i / (steps - 1)) * (maxRaw - minRaw);
    const calibration = { points: sorted };
    const output = applyFuelCalibration(raw, calibration);
    result.push({ raw: Math.round(raw * 1000) / 1000, output: Math.round(output * 1000) / 1000 });
  }

  return result;
}
